import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { formatApiError } from "@/lib/format";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2 } from "lucide-react";

const SHAKTI_BG =
  "https://static.prod-images.emergentagent.com/jobs/a38e0bc3-e5e1-4946-a4e2-33aa200f541b/images/0b58cdf3e407c2daf00e0d78ca68039fce6786f2b2f0fd70f1bb9cfad2707114.png";

const FORMATS = [
  { k: "M1", name: "Standard Hub", size: "1000-1500 sqft", inv: "₹40L–60L" },
  { k: "M2", name: "Compact Hub (Tier 3)", size: "500-800 sqft", inv: "₹20L–35L" },
  { k: "M3", name: "Flagship (Metro)", size: "2000-3000 sqft", inv: "₹80L–1.5Cr" },
];

export default function FranchiseApply() {
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    applicant_name: "",
    email: "",
    phone: "",
    city: "",
    state: "",
    format: "M1",
    investment_range: "₹40L–60L",
    profile: "",
    message: "",
  });
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) =>
    setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/franchise/apply", form);
      setDone(true);
      toast.success("Application submitted");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-[#0A0908] flex items-center justify-center p-6 relative">
        <img src={SHAKTI_BG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-[0.06]" />
        <div className="relative rounded-3xl border border-white/10 bg-white/[0.02] p-12 max-w-md text-center">
          <CheckCircle2 size={56} className="mx-auto text-[#E8D6A3] mb-6" />
          <h1 className="text-4xl font-black mb-3">We received it.</h1>
          <p className="text-zinc-400 mb-8">
            Our expansion team will reach out within 72 hours. Welcome to the
            Mirrorloops movement.
          </p>
          <Button
            onClick={() => nav("/")}
            data-testid="franchise-done-home-btn"
            className="bg-[#D4B878] hover:bg-[#E8D6A3] rounded-full px-8"
          >
            Back home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0908] relative">
      <img src={SHAKTI_BG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-[0.06]" />
      <Link
        to="/"
        className="absolute top-6 left-6 flex items-center gap-2 text-zinc-400 hover:text-white text-sm"
        data-testid="franchise-back-link"
      >
        <ArrowLeft size={16} /> Back
      </Link>
      <div className="relative max-w-3xl mx-auto px-6 md:px-12 py-24">
        <div className="text-xs uppercase tracking-[0.22em] text-[#E8D6A3] mb-4">
          Franchise application
        </div>
        <h1 className="text-5xl md:text-6xl font-black tracking-tighter mb-3">
          Own a Hub.
        </h1>
        <p className="text-zinc-400 mb-12">Step {step} of 3</p>

        <div className="rounded-3xl border border-white/10 bg-white/[0.02] p-8 md:p-10 backdrop-blur-xl">
          {step === 1 && (
            <div className="space-y-5">
              <h2 className="text-2xl font-bold mb-4">About you</h2>
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Full name</Label>
                <Input
                  data-testid="franchise-name-input"
                  value={form.applicant_name}
                  onChange={set("applicant_name")}
                  required
                  className="mt-2 bg-white/5 border-white/10"
                />
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Email</Label>
                  <Input
                    type="email"
                    data-testid="franchise-email-input"
                    value={form.email}
                    onChange={set("email")}
                    className="mt-2 bg-white/5 border-white/10"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Phone</Label>
                  <Input
                    data-testid="franchise-phone-input"
                    value={form.phone}
                    onChange={set("phone")}
                    className="mt-2 bg-white/5 border-white/10"
                    placeholder="+91-"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">
                  Which best describes you?
                </Label>
                <Select value={form.profile} onValueChange={set("profile")}>
                  <SelectTrigger
                    data-testid="franchise-profile-select"
                    className="mt-2 bg-white/5 border-white/10"
                  >
                    <SelectValue placeholder="Select profile" />
                  </SelectTrigger>
                  <SelectContent>
                    {[
                      "Local business owner",
                      "Real estate owner",
                      "Education entrepreneur",
                      "Cyber café operator",
                      "Coaching center owner",
                      "Other",
                    ].map((o) => (
                      <SelectItem key={o} value={o}>
                        {o}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="pt-4 flex justify-end">
                <Button
                  onClick={() => setStep(2)}
                  data-testid="franchise-step1-next-btn"
                  disabled={!form.applicant_name || !form.email || !form.phone || !form.profile}
                  className="bg-[#D4B878] hover:bg-[#E8D6A3] rounded-full px-8"
                >
                  Continue
                </Button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold mb-4">Choose a format</h2>
              <div className="grid md:grid-cols-3 gap-4">
                {FORMATS.map((f) => {
                  const active = form.format === f.k;
                  return (
                    <button
                      type="button"
                      key={f.k}
                      data-testid={`franchise-format-${f.k}`}
                      onClick={() => setForm({ ...form, format: f.k, investment_range: f.inv })}
                      className={`text-left rounded-2xl border p-5 transition ${
                        active
                          ? "border-[#D4B878] bg-[#D4B878]/5"
                          : "border-white/10 bg-white/[0.02] hover:bg-white/[0.04]"
                      }`}
                    >
                      <div className="text-3xl font-black">{f.k}</div>
                      <div className="text-sm text-zinc-300 mt-1 font-semibold">{f.name}</div>
                      <div className="text-xs text-zinc-500 mt-2">{f.size}</div>
                      <div className="text-sm text-[#E8D6A3] mt-3 font-bold">{f.inv}</div>
                    </button>
                  );
                })}
              </div>
              <div className="grid md:grid-cols-2 gap-4 pt-4">
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">City</Label>
                  <Input
                    data-testid="franchise-city-input"
                    value={form.city}
                    onChange={set("city")}
                    className="mt-2 bg-white/5 border-white/10"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">State</Label>
                  <Input
                    data-testid="franchise-state-input"
                    value={form.state}
                    onChange={set("state")}
                    className="mt-2 bg-white/5 border-white/10"
                  />
                </div>
              </div>
              <div className="flex justify-between pt-4">
                <Button
                  variant="outline"
                  onClick={() => setStep(1)}
                  className="rounded-full"
                  data-testid="franchise-step2-back-btn"
                >
                  Back
                </Button>
                <Button
                  onClick={() => setStep(3)}
                  disabled={!form.city}
                  data-testid="franchise-step2-next-btn"
                  className="bg-[#D4B878] hover:bg-[#E8D6A3] rounded-full px-8"
                >
                  Continue
                </Button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-5">
              <h2 className="text-2xl font-bold mb-4">Anything else?</h2>
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">
                  Your message (optional)
                </Label>
                <Textarea
                  data-testid="franchise-message-textarea"
                  rows={5}
                  value={form.message}
                  onChange={set("message")}
                  placeholder="Tell us about your timeline, local context…"
                  className="mt-2 bg-white/5 border-white/10"
                />
              </div>
              <div className="rounded-2xl border border-white/10 p-5 bg-white/[0.02] text-sm text-zinc-400">
                <div className="font-bold text-white mb-2">Summary</div>
                <div>{form.applicant_name} · {form.profile}</div>
                <div>
                  {form.city}, {form.state} · Format {form.format} ·{" "}
                  {form.investment_range}
                </div>
              </div>
              <div className="flex justify-between pt-4">
                <Button
                  variant="outline"
                  onClick={() => setStep(2)}
                  className="rounded-full"
                  data-testid="franchise-step3-back-btn"
                >
                  Back
                </Button>
                <Button
                  onClick={submit}
                  disabled={busy}
                  data-testid="franchise-submit-btn"
                  className="bg-[#D4B878] hover:bg-[#E8D6A3] rounded-full px-8"
                >
                  {busy ? "Submitting…" : "Submit application"}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
